import React, { useCallback } from "react";
import { connect } from "react-redux";

import Col from "antd/es/col";
import Row from "antd/es/row";

import { EntityTypesSupportedByIntegrations } from "@mapmycustomers/shared/types/entity";
import User from "@mapmycustomers/shared/types/User";

import {
  changeIntegrationUser,
  changeIntegrationUserSync,
  changeSyncOption,
  changeUnassignedRecordsOwner,
} from "@app/store/integrations/actions";
import { getCurrentIntegration, getIntegrationUsers } from "@app/store/integrations/selectors";
import { ServiceDescription } from "@app/store/integrations/supported-services";
import { getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import Integration from "@app/types/integrations/Integration";
import IntegrationUser from "@app/types/integrations/IntegrationUser";
import getFeaturePathByEntityType from "@app/util/analytic/getFeaturePathByEntityType";

import { getIntegrationAnalyticsSetupIssuer } from "../../integrationAnalytics";

import EntitySyncSettings from "./EntitySyncSettings";
import StepWrapper from "./StepWrapper";
import UserMapping from "./UserMapping";

export interface IntegrationUserWithSyncUnmappedUsers extends IntegrationUser {
  syncUnmappedUsers?: boolean;
}

interface Props {
  changeSyncOption: typeof changeSyncOption;
  changeUnassignedRecordsOwner: typeof changeUnassignedRecordsOwner;
  changeUserMapping: typeof changeIntegrationUser;
  currentIntegration?: Integration;
  integrationUsers: IntegrationUser[];
  mmcUsers: User[];
  selectedService: ServiceDescription;
  supportedEntityTypes: EntityTypesSupportedByIntegrations[];
  syncOptions: Integration["syncOptions"];
}

const Step1: React.FC<Props> = ({
  changeSyncOption,
  changeUnassignedRecordsOwner,
  changeUserMapping,
  currentIntegration,
  integrationUsers,
  mmcUsers,
  selectedService,
  supportedEntityTypes,
  syncOptions,
}) => {
  const handleSyncOptionChange = useCallback(
    (entityType, incoming, outgoing) => {
      changeSyncOption({ entityType, incoming, outgoing });
      getIntegrationAnalyticsSetupIssuer(currentIntegration?.type, 1).clicked([
        `Toggle ${getFeaturePathByEntityType(entityType)} ${incoming || outgoing ? "On" : "Off"}`,
      ]);
    },
    [changeSyncOption, currentIntegration]
  );

  const handleChangeUserMapping = useCallback(
    (payload) => {
      changeUserMapping(payload);
      getIntegrationAnalyticsSetupIssuer(currentIntegration?.type, 1).clicked(["Change User"], {
        payload,
      });
    },
    [changeUserMapping, currentIntegration?.type]
  );

  return (
    <StepWrapper>
      {supportedEntityTypes.length > 0 && (
        <Row justify="space-between">
          <Col sm={{ span: 24 }} xl={{ span: 6 }}>
            <EntitySyncSettings
              onSyncOptionChange={handleSyncOptionChange}
              supportedEntityTypes={supportedEntityTypes}
              syncOptions={syncOptions}
            />
          </Col>
          <Col sm={{ span: 24 }} xl={{ span: 17 }}>
            <UserMapping
              integration={currentIntegration}
              integrationUsers={integrationUsers}
              mmcUsers={mmcUsers}
              onChangeUnassignedRecordsOwner={changeUnassignedRecordsOwner}
              onChangeUserMapping={handleChangeUserMapping}
              selectedService={selectedService}
            />
          </Col>
        </Row>
      )}
    </StepWrapper>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentIntegration: getCurrentIntegration(state),
  integrationUsers: getIntegrationUsers(state),
  mmcUsers: getUsers(state),
});

const mapDispatchToProps = {
  changeSyncOption: changeSyncOption,
  changeUnassignedRecordsOwner,
  changeUserMapping: changeIntegrationUser,
  changeUserSyncing: changeIntegrationUserSync,
};

export default connect(mapStateToProps, mapDispatchToProps)(Step1);
